/** @jsx jsx */
import React, { Component, createRef } from 'react';
import { cssMap, jsx, styled } from '@compiled/react';
import debounce from 'lodash/debounce';
import findIndex from 'lodash/findIndex';
import noop from 'lodash/noop';
import {
	type InputProps,
	type MenuProps,
	type ControlProps,
	type GroupType,
	type AriaOnFocusProps,
	PopupSelect,
	components,
} from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import ComponentWithAnalytics from '@atlassian/jira-analytics-web-react/src/utils/component-with-analytics.tsx';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { SEARCH_DEBOUNCE_TIME } from '../../model/constants.tsx';
import type {
	Item,
	RenderTrigger,
	OnOpenChange,
	OnClose,
	OnClick,
	ItemGroup,
	OnShowMoreClick,
	OnMouseEnter,
} from '../../model/index.tsx';
import { controlHeight } from './control/index.tsx';
import Control from './control/view.tsx';
import CreateButton from './create-button/view.tsx';
import Group from './group/view.tsx';
import Input from './input/view.tsx';
import Loading from './loading/view.tsx';
import MenuList from './menu-list/view.tsx';
import messages from './messages.tsx';
import Option from './option/view.tsx';

const createButtonHeight = 52;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isOptionSelected = (option: any, selected: any) =>
	selected && selected.length === 1 && option.key === selected[0].key;

const filterOption = () => true;

type Props = {
	isDefaultOpen: boolean;
	isLoading: boolean;
	isSearchEnabled: boolean;
	isError: boolean;
	items: ItemGroup[] | undefined;
	errorMessage: string;
	selectedItem: Item | undefined;
	createLabel: string | undefined;
	searchValue: string;
	searchPlaceholder: string;
	renderTrigger: RenderTrigger;
	listHeading: string | undefined;
	onCreate: () => void;
	onRetry: () => void;
	onShowMore: OnShowMoreClick;
	onOpenChange: OnOpenChange;
	onClose: OnClose;
	onSearchChange: (arg1: string) => void;
	onClick: OnClick;
	onMouseEnter: OnMouseEnter;
};

type State = {
	isOpen: boolean;
	lastMenuHeight: number | null;
	searchValue: string;
	triggerWidth: number;
};

// eslint-disable-next-line jira/react/no-class-components
export class Switcher extends Component<Props, State> {
	static defaultProps = {
		isDefaultOpen: false,
		isLoading: false,
		isSearchEnabled: false,
		isError: false,
		items: undefined,
		createLabel: '',
		searchValue: '',
		searchPlaceholder: '',
		errorMessage: '',
		selectedItem: undefined,
		listHeading: undefined,
		onOpenChange: noop,
		onClose: noop,
		onSearchChange: noop,
		onCreate: noop,
		onClick: noop,
		onRetry: noop,
		onShowMore: noop,
		onMouseEnter: noop,
	};

	state = {
		isOpen: this.props.isDefaultOpen,
		lastMenuHeight: null,
		searchValue: this.props.searchValue,
		triggerWidth: gridSize * 26,
	};

	componentDidMount() {
		if (this.props.isDefaultOpen) {
			this.onOpen();
		}
	}

	componentDidUpdate(prevProps: Props) {
		// UI code
		const { searchValue } = this.props;
		if (prevProps.searchValue !== searchValue) {
			// this is guarded, so it's legit
			this.setState({ searchValue });
		}

		// Analytics code
		if (this.state.isOpen) {
			// @ts-expect-error - TS2540 - Cannot assign to 'current' because it is a read-only property.
			this.hasBoardSwitcherBeenOpened.current = true;
			if (!this.prevSwitcherOpenStateRef.current) {
				// @ts-expect-error - TS2540 - Cannot assign to 'current' because it is a read-only property.
				this.hasUserInteractedWithSwitcherViaClickRef.current = null;
				// @ts-expect-error - TS2540 - Cannot assign to 'current' because it is a read-only property.
				this.hasUserInteractedWithSwitcherViaEnterKeyDownRef.current = null;
			}
		} else {
			// eslint-disable-next-line no-lonely-if
			if (
				!this.hasUserInteractedWithSwitcherViaClickRef.current &&
				!this.hasUserInteractedWithSwitcherViaEnterKeyDownRef.current &&
				this.hasBoardSwitcherBeenOpened.current
			) {
				this.props.onClose({
					closedWithoutInteracting: true,
				});
			}
		}
		// Analytics code
		// @ts-expect-error - TS2540 - Cannot assign to 'current' because it is a read-only property.
		this.prevSwitcherOpenStateRef.current = this.state.isOpen;
	}

	// These refs store whether the user has interacted with the switcher or any of its child components
	// via recording whether an onClick or onKeyDown 'Enter' event occurred.
	// These refs are used to measure the analytics of when this component was opened, and then closed without
	// being used.
	// Click and KeyDown must be separated since onClick is called when create a board action is rendered via
	// the Enter key (accessibility usage)
	hasBoardSwitcherBeenOpened = createRef<boolean>();

	hasUserInteractedWithSwitcherViaClickRef = createRef<boolean>();

	prevSwitcherOpenStateRef = createRef<boolean>();

	hasUserInteractedWithSwitcherViaEnterKeyDownRef = createRef<boolean>();

	onSearchChange = (value: string) => {
		if (value.length) {
			this.debouncedOnChange(value);
		} else {
			this.debouncedOnChange.cancel();
			this.props.onSearchChange(value);
		}
		this.setState({ searchValue: value });
	};

	onOpenChange({ isOpen }: { isOpen: boolean }) {
		if (this.state.isOpen === isOpen) {
			return;
		}
		this.setState({ isOpen, lastMenuHeight: null });
		this.props.onOpenChange({ isOpen });
	}

	onOpen = () => {
		this.onOpenChange({ isOpen: true });
		if (this.switcherRef.current) {
			const { width } = this.switcherRef.current.getBoundingClientRect();
			this.setState({ triggerWidth: width });
		}
	};

	onClose = () => {
		this.onOpenChange({ isOpen: false });
	};

	onSelectChange = (value: Item) => {
		const section = findIndex(this.props.items, (itemsGroup: ItemGroup) =>
			itemsGroup.options.includes(value),
		);
		this.props.onClick(value, section);
	};

	onBeforeLoading = ({ height }: { height: number }) => {
		this.setState({ lastMenuHeight: height });
	};

	onCreate = () => {
		this.props.onCreate();
		this.closeSwitcher();
	};

	getMenuHeight() {
		return this.isCreateButtonAvailable() ? menuHeight - createButtonHeight : menuHeight;
	}

	onClick = () => {
		// Analytics code
		// This does not represent a user interaction, so ignore it if the ref has not been initialized
		if (this.hasUserInteractedWithSwitcherViaClickRef.current === null) {
			// Initialize the ref to pick up user interactions
			// @ts-expect-error - TS2540 - Cannot assign to 'current' because it is a read-only property.
			this.hasUserInteractedWithSwitcherViaClickRef.current = false;
			return;
		}
		// @ts-expect-error - TS2540 - Cannot assign to 'current' because it is a read-only property.
		this.hasUserInteractedWithSwitcherViaClickRef.current = true;
	};

	onKeyDown = (event: KeyboardEvent) => {
		// Analytics code
		if (event.key === 'Enter' && this.hasUserInteractedWithSwitcherViaClickRef.current !== null) {
			// @ts-expect-error - TS2540 - Cannot assign to 'current' because it is a read-only property.
			this.hasUserInteractedWithSwitcherViaEnterKeyDownRef.current = true;
		}
	};

	getLoadingHeight() {
		const { lastMenuHeight } = this.state;
		if (lastMenuHeight) {
			return lastMenuHeight;
		}
		return this.getMenuHeight() - controlHeight;
	}

	switcherRef = createRef<HTMLDivElement>();

	popupRef = createRef();

	debouncedOnChange = debounce(this.props.onSearchChange, SEARCH_DEBOUNCE_TIME);

	closeSwitcher() {
		// @ts-expect-error - TS2571 - Object is of type 'unknown'.
		this.popupRef.current && this.popupRef.current.close();
	}

	isCreateButtonAvailable() {
		return !!this.props.createLabel;
	}

	popperProps() {
		const placement = 'bottom-start';
		return {
			placement,
			strategy: 'fixed',
			modifiers: [
				{
					name: 'preventOverflow',
					options: {
						padding: 5,
						mainAxis: false, // true by default
					},
				},

				{
					name: 'offset',
					options: {
						offset: [0, gridSize],
					},
				},
				{
					name: 'hide',
					options: {
						padding: 5,
					},
				},
				{
					name: 'flip',
					options: {
						padding: 5,
					},
				},
			],
		};
	}

	renderLoading = () => this.props.isLoading && <Loading height={this.getLoadingHeight()} />;

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	renderMenuList = (props: any) => {
		const { isError, errorMessage, onRetry, listHeading } = this.props;

		return (
			<MenuList
				{...props}
				isError={isError}
				errorMessage={errorMessage}
				listHeading={listHeading}
				onBeforeLoading={this.onBeforeLoading}
				onRetry={onRetry}
			/>
		);
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	renderGroupHeading = (props: any) => {
		const { children, data } = props;
		// Suppressed due to new lint violation when upgrading to eslint-plugin-react-hooks v5
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const { formatMessage } = useIntl();
		const ariaLabel =
			children && children[0] ? data?.label : formatMessage(messages.otherRecentBoards);
		return children ? (
			<components.GroupHeading
				{...props}
				role="group"
				aria-label={ariaLabel}
				xcss={groupHeadingStyles.root}
			/>
		) : null;
	};

	renderControl = (selectControlProps: ControlProps<ItemGroup>) => {
		const { isSearchEnabled } = this.props;

		return <Control isSearchEnabled={isSearchEnabled} {...selectControlProps} />;
	};

	renderInput = (selectInputProps: InputProps<ItemGroup>) => {
		const { searchPlaceholder } = this.props;
		const { searchValue } = this.state;

		return (
			<Input
				onChange={this.onSearchChange}
				value={searchValue}
				placeholder={searchPlaceholder}
				inputProps={selectInputProps}
			/>
		);
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	renderGroup = (props: any) => <Group {...props} onShowMore={this.props.onShowMore} />;

	renderFooter() {
		const { createLabel } = this.props;

		return (
			this.isCreateButtonAvailable() && (
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
				<CreateButton label={createLabel as any} onClick={this.onCreate} />
			)
		);
	}

	renderMenu = (props: MenuProps<ItemGroup>) => {
		const { innerRef, innerProps, children } = props;
		return (
			<div ref={innerRef} {...innerProps}>
				{children}
			</div>
		);
	};

	getOptionLabel = (option: Item) => {
		let label = option.text;
		if (option.subText) {
			label += ` ${option.subText}`;
		}
		return label;
	};

	render() {
		const { isLoading, selectedItem, items, renderTrigger, onMouseEnter } = this.props;
		const { isOpen, searchValue, triggerWidth } = this.state;

		const onFocus = (
			onFocusProps: AriaOnFocusProps<Item, GroupType<Item>>,
			defaultOptions?: ItemGroup[],
		) => {
			const { focused } = onFocusProps;
			const isOptionFocused = (option: Item) => option.text === focused.text;
			const groupData = defaultOptions?.find((option: ItemGroup) =>
				option.options?.some(isOptionFocused),
			);
			const groupOptionIndex = groupData?.options.findIndex(isOptionFocused) ?? 0;

			return `Option ${this.getOptionLabel(focused)}, ${groupData?.label} group, item ${
				groupOptionIndex + 1
			} out of ${groupData?.options.length}. All in all `;
		};

		// Helper function which identifies if options are grouped.
		const isOptionsGrouped = (arr?: ItemGroup[]) =>
			arr?.every((obj) => Object.hasOwn(obj, 'options'));

		// @ts-expect-error - TS7031 - Binding element 'ref' implicitly has an 'any' type.
		const createTriggerRenderer = ({ ref }) =>
			renderTrigger({
				isOpen,
				onMouseEnter,
				targetRef: ref,
			});

		const renderPopupSelect = () => (
			// @ts-expect-error - TS2769 - No overload matches this call.
			<PopupSelect
				ref={this.popupRef}
				onOpen={this.onOpen}
				onClose={this.onClose}
				autoFocus
				maxMenuWidth={triggerWidth}
				minMenuWidth={triggerWidth}
				backspaceRemovesValue={false}
				classNamePrefix="switcher"
				components={{
					Control: this.renderControl,
					Input: this.renderInput,
					DropdownIndicator: noop,
					Placeholder: noop,
					IndicatorsContainer: noop,
					LoadingIndicator: noop,
					Option,
					Menu: this.renderMenu,
					Group: this.renderGroup,
					GroupHeading: this.renderGroupHeading,
					MenuList: this.renderMenuList,
					LoadingMessage: this.renderLoading,
					NoOptionsMessage: this.renderLoading,
				}}
				getOptionLabel={this.getOptionLabel}
				controlShouldRenderValue={false}
				isLoading={isLoading}
				isClearable={false}
				searchThreshold={-1}
				isOptionSelected={isOptionSelected}
				inputValue={searchValue}
				menuIsOpen
				// react-select does not respect 'isLoading' parameter in not async version
				// that's why we use NoOptionsMessage to display loading state
				// as we'll always have at least one option - view all link
				options={isLoading || !items ? [] : items}
				maxMenuHeight={this.getMenuHeight()}
				onChange={this.onSelectChange}
				filterOption={filterOption}
				styles={selectStyles()}
				tabSelectsValue={false}
				value={selectedItem}
				footer={this.renderFooter()}
				target={createTriggerRenderer}
				popperProps={this.popperProps()}
				defaultIsOpen={this.props.isDefaultOpen}
				ariaLiveMessages={
					isOptionsGrouped(this.props.items)
						? {
								onFocus: (data) => onFocus(data, this.props.items),
							}
						: undefined
				}
			/>
		);

		return (
			// In styled there is used a z-index hack to increase z-index
			// value of components inside SwitcherContainer. More info
			// in styled file
			<SwitcherContainer
				ref={this.switcherRef}
				onClick={this.onClick}
				// @ts-expect-error - TS2322 - Type '(event: KeyboardEvent) => void' is not assignable to type 'KeyboardEventHandler<HTMLDivElement>'.
				onKeyDown={this.onKeyDown}
			>
				{renderPopupSelect()}
			</SwitcherContainer>
		);
	}
}

export default ComponentWithAnalytics('switcherMenu', {
	onClick: 'clicked',
	onOpenChange: 'opened',
	onClose: 'closed',
	onCreate: 'clicked',
	onRetry: 'clicked',
	onSearchChange: 'typed',
	onShowMore: 'clicked',
})(Switcher);

// This is z-index hack for the navigation
// In navigation there are some items which are positioned using `z-index`
// and value is the same as popup select
// https://jdog.jira-dev.com/browse/KITKAT-936
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SwitcherContainer = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&&& > *': {
		zIndex: 10,
	},
});

const menuHeight = 53 * gridSize;

const groupHeadingStyles = cssMap({
	root: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors,@atlaskit/ui-styling-standard/no-nested-selectors -- To migrate as part of go/ui-styling-standard
		'&&&': {
			width: 'inherit',
			display: 'flex',
		},
	},
});

const selectStyles = () => ({
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	valueContainer: (defaultStyles: any) => ({
		...defaultStyles,
		paddingTop: 0,
		paddingRight: 0,
		paddingBottom: 0,
		paddingLeft: 0,
	}),
	menu: noop,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	groupHeading: (defaultStyles: any) => ({
		...defaultStyles,
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
		marginBottom: 0,
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
		position: 'sticky',
		top: 0,
		zIndex: 2,
		backgroundColor: token('elevation.surface'),
		display: 'flex',
		flexDirection: 'row',
		'&&&': {
			width: 'inherit',
			display: 'flex',
		},
	}),
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	group: (defaultStyles: any) => ({
		...defaultStyles,
		paddingTop: 0,
		paddingBottom: 0,
	}),
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	menuList: (defaultStyles: any) => ({
		...defaultStyles,
		zIndex: 1,
		position: 'relative',
		paddingTop: 0,
		paddingBottom: 0,
	}),
});
